import React from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertTriangle, CalendarClock, CreditCard, Check } from "lucide-react";
import { SubscriptionPlan, SubscriptionProps } from "./SubscriptionPlans";
import { cn } from "@/lib/utils";

interface SubscriptionStatusProps {
  currentPlan: SubscriptionPlan[];
  renewalDate?: string;
  paymentMethod?: {
    type: string;
    last4?: string;
    expiryDate?: string;
  };
  onManageSubscription: () => void;
  onCancelSubscription: () => void;
  accountType?: {
    account_type: string;
    status: string;
    valid_until: null | string;
  };
}

const SubscriptionStatus: React.FC<SubscriptionStatusProps> = ({
  currentPlan,
  renewalDate,
  paymentMethod,
  onManageSubscription,
  onCancelSubscription,
  accountType,
}) => {
  // const isFree = currentPlan?.price === "Free";
  const isFree = accountType?.account_type === "standard";

  const activePlan = currentPlan.find(
    (plan) => plan.id === accountType?.account_type
  );
  return (
    <Card>
      <CardHeader>
        <CardTitle>Current Plan</CardTitle>
        <CardDescription>Your subscription details</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium capitalize">
              {accountType?.account_type}
            </h3>
            <p className="text-sm text-muted-foreground">
              {/* {typeof currentPlan?.price === "number"
                ? `$${currentPlan?.price}/${currentPlan?.billingPeriod}`
                : currentPlan?.price} */}

              {isFree ? (
                <span className="text-sm text-muted-foreground">(Free)</span>
              ) : (
                <span className="text-sm text-muted-foreground">(Paid)</span>
              )}
            </p>
          </div>
          <div className="flex flex-col items-end gap-1">
            <span
              className={cn(
                "flex items-center gap-1 bg-green-100 text-green-800 text-xs font-semibold px-3 py-1 rounded-full capitalize",
                {
                  "bg-gray-100 text-gray-800":
                    accountType?.status === "inactive",
                }
              )}
            >
              {accountType?.status === "active" && <Check className="w-4 " />}{" "}
              {accountType?.status}
            </span>
            {accountType?.valid_until && (
              <span className="bg-gray-100 text-gray-800 text-xs font-semibold px-3 py-1 rounded-full">
                <span className="font-bold">Valid Till:</span>{" "}
                {new Date(accountType?.valid_until).toLocaleString("en-GB")}
              </span>
            )}
          </div>
        </div>

        {!isFree && (
          <>
            {renewalDate && (
              <div className="flex items-center text-sm">
                <CalendarClock className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>
                  Next renewal on <strong>{renewalDate}</strong>
                </span>
              </div>
            )}

            {paymentMethod && (
              <div className="flex items-center text-sm">
                <CreditCard className="h-4 w-4 mr-2 text-muted-foreground" />
                <span>
                  {paymentMethod?.type}{" "}
                  {paymentMethod?.last4 && `ending in ${paymentMethod?.last4}`}{" "}
                  {paymentMethod?.expiryDate &&
                    `(expires ${paymentMethod?.expiryDate})`}
                </span>
              </div>
            )}
          </>
        )}

        <div className="pt-2">
          <h4 className="text-sm font-medium mb-2">Plan Features</h4>
          {/* <ul className="space-y-1">
            {currentPlan?.features
              .filter((feature) => feature?.included)
              .map((feature, index) => (
                <li key={index} className="flex items-center text-sm">
                  <Check className="h-4 w-4 text-green-500 mr-2" />
                  {feature.name}
                </li>
              ))}
          </ul> */}

          <ul className="space-y-1">
            {activePlan?.features.map((feature, index) => (
              <li key={index} className="flex items-center text-sm">
                <Check className="h-4 w-4 text-green-500 mr-2" />
                {typeof feature === "string" ? feature : feature.name}
              </li>
            ))}
          </ul>
        </div>
      </CardContent>
      <CardFooter className="flex flex-col space-y-2">
        {isFree && (
          <Button
            onClick={onManageSubscription}
            className="w-full"
            variant={"default"}
          >
            Upgrade Plan
          </Button>
        )}

        {/* <Button
          onClick={onManageSubscription}
          className="w-full"
          variant={isFree ? "default" : "outline"}
        >
          {isFree ? "Upgrade Plan" : "Manage Subscription"}
        </Button> */}
        {/* {!isFree && (
          <Button
            onClick={onCancelSubscription}
            variant="ghost"
            className="w-full text-destructive hover:text-destructive/90"
          >
            Cancel Subscription
          </Button>
        )} */}
      </CardFooter>
    </Card>
  );
};

export default SubscriptionStatus;
